import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/store/session';
import { usersApi } from '@/features/users/api';
import { saveAuthData, clearAuthData } from '@/core/auth';
import { apiClient } from '@/core/apiClient';
import { Loader2, Save, Shield, RefreshCw } from 'lucide-react';
import { AdoptionWaitRequestCard } from '@/features/adoption-wait/AdoptionWaitRequestCard';

const profileSchema = z.object({
  fullName: z.string().min(1, 'Nombre es obligatorio').max(120),
  phone: z.string().max(30).optional(),
});

type ProfileForm = z.infer<typeof profileSchema>;

const formatPhone = (digits: string) => {
  const clean = digits.replace(/\D/g, '').slice(0, 10);
  const part1 = clean.slice(0, 3);
  const part2 = clean.slice(3, 6);
  const part3 = clean.slice(6, 10);
  return [part1, part2, part3].filter(Boolean).join(' ');
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, token, isCustomer } = useAuth();
  const { setSession, clearSession } = useSession();
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const initialValues: ProfileForm = useMemo(
    () => ({
      fullName: (user as any)?.full_name || (user as any)?.fullName || '',
      phone: formatPhone((user as any)?.phone || ''),
    }),
    [user]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const onSubmit = async (data: ProfileForm) => {
    if (!user?.id) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se encontró el usuario en la sesión.' });
      return;
    }
    const payload: any = {};
    if (dirtyFields.fullName) payload.fullName = data.fullName;
    if (dirtyFields.phone) {
      const digits = (data.phone || '').replace(/\D/g, '');
      payload.phone = digits || null;
    }
    if (!Object.keys(payload).length) {
      toast({ title: 'Sin cambios', description: 'No hay modificaciones para guardar.' });
      return;
    }
    try {
      setIsSaving(true);
      const updated = await usersApi.update(Number(user.id), payload);
      const normalizedUser = {
        ...(user as any),
        full_name: (updated as any).full_name ?? (updated as any).fullName ?? data.fullName,
        phone: (updated as any).phone ?? (data.phone || ''),
      };
      if (token) {
        saveAuthData(token, normalizedUser as any);
        setSession(token, normalizedUser as any);
      }
      toast({ title: 'Datos actualizados', description: 'Tus datos personales han sido guardados.' });
      reset({
        fullName: normalizedUser.full_name,
        phone: formatPhone(normalizedUser.phone || ''),
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'No se pudieron guardar los cambios.';
      toast({ variant: 'destructive', title: 'Error', description: message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se encontró el correo del usuario.' });
      return;
    }
    try {
      setIsResetting(true);
      await apiClient.post('/auth/forgot-password', { email: user.email });
      clearAuthData();
      clearSession();
      navigate(`/reset-password?email=${encodeURIComponent(user.email)}`);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'No se pudo iniciar el proceso de recuperación.';
      toast({ variant: 'destructive', title: 'Error', description: message });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Mi Perfil</h1>
            <p className="text-muted-foreground text-sm">Actualiza tus datos personales o redefine tu contraseña.</p>
          </div>
        </div>

        <Card className="p-6 space-y-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Nombre completo</Label>
              <Input id="fullName" {...register('fullName')} placeholder="Tu nombre" />
              {errors.fullName && <p className="text-sm text-destructive">{errors.fullName.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo</Label>
              <Input id="email" value={(user as any)?.email || ''} readOnly disabled />
              <p className="text-xs text-muted-foreground">El correo no puede ser editado.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                {...register('phone', {
                  onChange: (e) => {
                    const formatted = formatPhone(e.target.value);
                    setValue('phone', formatted, { shouldDirty: true });
                  },
                })}
                value={watch('phone') || ''}
                placeholder="000 000 0000"
                inputMode="tel"
              />
              {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button type="button" variant="outline" onClick={handleResetPassword} disabled={isResetting}>
                {isResetting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Redefinir contraseña
                  </>
                )}
              </Button>
              <Button type="submit" disabled={isSaving} className="sm:min-w-[160px]">
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar cambios
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
        {isCustomer && <AdoptionWaitRequestCard />}
      </div>
    </Layout>
  );
}
